import Image from "next/image";
import React from "react";
import { motion, MotionStyle } from "motion/react";

interface HowItWorksStepCardProps {
  step: number;
  title: string;
  lines: string[]; // description lines
  imageSrc: string; // path to the illustrative image
  imageAlt?: string; // optional custom alt text
  imageWidth?: number; // override default width
  imageHeight?: number; // override default height
  imageClassName?: string; // optional custom class name for image
  className?: string;
  style?: MotionStyle; // optional inline styles for container (supports motion values)
}

// Presentational card replicating provided design (dark gradient panel with numbered circle)
export function HowItWorksStepCard({
  step,
  title,
  lines,
  imageSrc,
  imageAlt,
  imageWidth = 645,
  imageHeight = 457,
  imageClassName = "",
  className = "",
  style,
}: HowItWorksStepCardProps) {
  return (
    <motion.div
      className={`lg:min-h-80 max-h-120 w-full border border-white/10 bg-neutral-950 p-10 md:px-26.5 md:py-42.5 flex flex-col lg:flex-row gap-6 items-center justify-between overflow-hidden ${className}`}
      style={style}
    >
      {/* Gradient / vignette overlay */}
      <div className="pointer-events-none z-0 absolute inset-0">
        <div className="absolute inset-0 backdrop-blur-[2px] mask-[linear-gradient(to_right,black,black,transparent)]" />
      </div>
      <div className="relative z-1 w-full flex flex-row gap-3 items-center lg:items-start">
        {/* Number and Title in one line */}
        <div className="flex items-center gap-3">
          <div className="relative w-10 h-10 shrink-0 rounded-full flex items-center justify-center border border-white/20 bg-white/5 backdrop-blur-sm text-white font-medium text-base">
            {step}
          </div>
        </div>
        {/* Text content */}
        <div className="relative max-w-xl text-left space-y-3">
          <h3 className="font-host-grotesk text-white text-xl md:text-2xl leading-snug">
            {title}
          </h3>
          {/* Mobile: Join lines with space */}
          <div className="lg:hidden text-sm md:text-base text-center leading-relaxed text-white/60">
            <p>{lines.join(" ")}</p>
          </div>
          {/* Desktop: Keep lines separate */}
          <div className="hidden lg:block space-y-1 text-sm md:text-base leading-relaxed text-white/60">
            {lines.map((l, i) => (
              <p key={i}>{l}</p>
            ))}
          </div>
        </div>
      </div>
      <div className="w-full lg:w-[50%] relative z-1 shrink-0 flex items-center justify-center">
        {/* Fixed size container with max constraints */}
        <div className="relative w-full h-full min-h-64 md:min-h-72 lg:min-h-80 max-w-120 max-h-120 rounded-2xl overflow-hidden">
          <Image
            src={imageSrc}
            alt={imageAlt ?? `Step ${step} image`}
            fill
            className={`object-contain ${imageClassName}`}
            sizes="(max-width: 1024px) 100vw, 50vw"
          />
        </div>
      </div>
    </motion.div>
  );
}

export default HowItWorksStepCard;
